import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';
import PrivacyPolicyModal from './PrivacyPolicyModal';

const CookieConsent = ({ darkMode }) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookieConsent', 'accepted');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowPrivacyModal(false);
  };

  const handleDecline = () => {
    localStorage.setItem('cookieConsent', 'declined');
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setShowBanner(false);
    setShowPrivacyModal(false);
  };

  return (
    <>
      <PrivacyPolicyModal
        darkMode={darkMode}
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        onAccept={handleAccept}
        onDecline={handleDecline}
      />

      <AnimatePresence>
        {showBanner && !showPrivacyModal && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={`fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6 ${
              darkMode 
                ? 'bg-slate-900/95 border-t border-white/10' 
                : 'bg-white/95 border-t border-blue-200'
            } backdrop-blur-lg shadow-2xl`}
          >
            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex-1">
                <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-white' : 'text-blue-950'}`}>
                  🍪 We Value Your Privacy
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  We use localStorage to save your preferences (like dark mode). We don't use tracking cookies or 
                  share your data with third parties. Read our{' '}
                  <button
                    onClick={() => setShowPrivacyModal(true)}
                    className={`underline font-medium hover:no-underline ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  >
                    Privacy Policy
                  </button>.
                </p>
              </div>
              
              <div className="flex gap-3 items-center">
                <button
                  onClick={handleDecline}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                    darkMode
                      ? 'text-gray-300 hover:text-white hover:bg-white/10'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Decline
                </button>
                <button
                  onClick={handleAccept}
                  className={`px-6 py-2 rounded-lg font-semibold transition-all transform hover:scale-105 ${
                    darkMode
                      ? 'bg-blue-600 hover:bg-blue-500 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  } shadow-lg`}
                >
                  Accept
                </button>
                <button
                  onClick={handleDecline}
                  className={`p-2 rounded-lg transition-colors ${
                    darkMode
                      ? 'hover:bg-white/10 text-gray-400'
                      : 'hover:bg-gray-100 text-gray-500'
                  }`}
                  aria-label="Close cookie consent"
                >
                  <X size={20} weight="bold" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieConsent;
